import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from "react";
import { FcCollapse } from "react-icons/fc";

interface BottomDialogContextType {
  openDialog: (content: ReactNode, header?: ReactNode) => void;
  closeDialog: () => void;
}

const BottomDialogContext =
  createContext<BottomDialogContextType | undefined>(undefined);

const SHEET_HEIGHT = "75vh"; // 🔧 change height here (60vh / 80vh / 90vh)

export const BottomDialogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode>(null);
  const [header, setHeader] = useState<ReactNode>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);

  const openDialog = useCallback(
    (dialogContent: ReactNode, dialogHeader?: ReactNode) => {
      previouslyFocused.current = document.activeElement as HTMLElement;
      setContent(dialogContent);
      setHeader(dialogHeader ?? null);
      setIsOpen(true);
    },
    []
  );

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setContent(null);
      setHeader(null);
      previouslyFocused.current?.focus();
    }, 250);
  }, []);

  /* Focus trap */
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDialog();

      if (e.key === "Tab") {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    first.focus();

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeDialog]);

  /* Hide background from screen readers */
  useEffect(() => {
    const bg = backgroundRef.current as HTMLDivElement & { inert?: boolean };
    if (!bg) return;

    if (isOpen) {
      bg.setAttribute("aria-hidden", "true");
      bg.inert = true;
    } else {
      bg.removeAttribute("aria-hidden");
      bg.inert = false;
    }
  }, [isOpen]);

  return (
    <BottomDialogContext.Provider value={{ openDialog, closeDialog }}>
      <div ref={backgroundRef}>{children}</div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center"
          role="dialog"
          aria-modal="true"
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeDialog}
          />

          {/* Bottom Sheet */}
          <div className="relative z-10 w-full max-w-2xl">
            <div
              ref={dialogRef}
              style={{ height: SHEET_HEIGHT }}
              className="
                bg-white dark:bg-gray-800 dark:text-gray-100
                w-full
                rounded-t-2xl
                shadow-2xl
                flex flex-col
                animate-slideUp
              "
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle */}
              <div className="flex justify-center py-2">
                <div className="w-10 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>

              {/* Header */}
              {header && (
                <div className="sticky top-0 z-20 px-4 h-[52px] flex items-center justify-between border-b bg-white dark:bg-gray-800 dark:border-gray-700">
                  <div className="font-semibold truncate">{header}</div>
                  <button
                    onClick={closeDialog}
                    aria-label="Close dialog"
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-200"
                  >
                    <FcCollapse size={22} />
                  </button>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                {content}
              </div>
            </div>
          </div>
        </div>
      )}
    </BottomDialogContext.Provider>
  );
};

export const useBottomDialog = (): BottomDialogContextType => {
  const context = useContext(BottomDialogContext);
  if (!context) {
    throw new Error(
      "useBottomDialog must be used within a BottomDialogProvider"
    );
  }
  return context;
};